import React, { useState, useEffect } from 'react';
import {
  PlusCircle,
  CreditCard,
  Edit,
  Trash2,
  X,
  Save,
  AlertTriangle,
  Check,
  XCircle,
} from 'lucide-react';
import styles from './TipiPagamentoPage.module.css';
import tipiPagamentoService, { TipoPagamento, TipoPagamentoRequest } from '../../services/tipiPagamentoService';
import { ExportDropdown, ExportFormat } from '@/components/common/ExportDropdown';
import { exportToExcel, exportToPdf, ExportColumn } from '@/utils/exports';

export const TipiPagamentoPage: React.FC = () => {
  // Data states
  const [tipiPagamento, setTipiPagamento] = useState<TipoPagamento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingTipo, setEditingTipo] = useState<TipoPagamento | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TipoPagamento | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    codice: '',
    nome: '',
    descrizione: '',
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState<{nome?: boolean; codice?: string}>({});
  const [formLoading, setFormLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Load data on mount
  useEffect(() => {
    loadTipiPagamento();
  }, []);

  const loadTipiPagamento = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await tipiPagamentoService.getTipiPagamento();
      setTipiPagamento(data);
    } catch (err) {
      console.error('Error loading tipi pagamento:', err);
      setError('Errore nel caricamento dei tipi pagamento');
    } finally {
      setLoading(false);
    }
  };

  // Open modal for new tipo
  const openNewModal = () => {
    setEditingTipo(null);
    setFormData({
      codice: '',
      nome: '',
      descrizione: '',
      isActive: true,
    });
    setFormErrors({});
    setShowFormModal(true);
  };

  // Open modal for editing
  const openEditModal = (tipo: TipoPagamento) => {
    setEditingTipo(tipo);
    setFormData({
      codice: tipo.codice,
      nome: tipo.nome,
      descrizione: tipo.descrizione || '',
      isActive: tipo.isActive,
    });
    setFormErrors({});
    setShowFormModal(true);
  };

  // Open delete confirmation
  const openDeleteModal = (tipo: TipoPagamento) => {
    setDeleteTarget(tipo);
    setShowDeleteModal(true);
  };

  // Handle form save
  const handleSave = async () => {
    // Validate required fields
    const errors: {nome?: boolean} = {};
    if (!formData.nome.trim()) errors.nome = true;

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      setFormLoading(true);

      const requestData: TipoPagamentoRequest = {
        codice: formData.codice || undefined,
        nome: formData.nome,
        descrizione: formData.descrizione || undefined,
        isActive: formData.isActive,
      };

      if (editingTipo) {
        await tipiPagamentoService.updateTipoPagamento(editingTipo.id, requestData);
      } else {
        await tipiPagamentoService.createTipoPagamento(requestData);
      }

      await loadTipiPagamento();
      setFormErrors({});
      setShowFormModal(false);
    } catch (err: unknown) {
      console.error('Error saving tipo pagamento:', err);
      const axiosError = err as { response?: { status?: number; data?: { message?: string } } };
      if (axiosError?.response?.status === 400) {
        const message = axiosError.response.data?.message || '';
        if (message.includes('codice') && message.includes('Esiste già')) {
          setFormErrors(prev => ({ ...prev, codice: 'Esiste già un tipo pagamento con questo codice' }));
          return;
        }
      }
      setError('Errore nel salvataggio del tipo pagamento');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    try {
      setLoading(true);
      await tipiPagamentoService.deleteTipoPagamento(deleteTarget.id);
      await loadTipiPagamento();
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error('Error deleting tipo pagamento:', err);
      setError('Errore nell\'eliminazione del tipo pagamento');
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors(prev => ({ ...prev, [field]: field === 'codice' ? undefined : false }));
    }
  };

  // Handle export
  const handleExport = async (format: ExportFormat) => {
    if (tipiPagamento.length === 0) {
      alert('Nessun dato da esportare');
      return;
    }

    setExportLoading(true);
    try {
      const columns: ExportColumn[] = [
        { field: 'codice', header: 'Codice', type: 'text', width: 15 },
        { field: 'nome', header: 'Nome', type: 'text', width: 25 },
        { field: 'descrizione', header: 'Descrizione', type: 'text', width: 35 },
        { field: 'isActiveDisplay', header: 'Attivo', type: 'text', width: 10 },
      ];

      const exportData = tipiPagamento.map(t => ({
        codice: t.codice,
        nome: t.nome,
        descrizione: t.descrizione || '',
        isActiveDisplay: t.isActive ? 'Sì' : 'No',
      }));

      const config = {
        title: 'Tipi Pagamento',
        filename: 'tipi_pagamento',
        columns,
        orientation: 'portrait' as const,
      };

      if (format === 'excel') {
        await exportToExcel(config, exportData);
      } else {
        await exportToPdf(config, exportData);
      }
    } catch (err) {
      console.error('Error exporting tipi pagamento:', err);
      setError('Errore durante l\'esportazione');
    } finally {
      setExportLoading(false);
    }
  };

  // Stats
  const totalTipi = tipiPagamento.length;
  const attivi = tipiPagamento.filter(t => t.isActive).length;
  const inattivi = tipiPagamento.filter(t => !t.isActive).length;

  return (
    <div className={styles.container}>
      <main>
        {/* Page Header */}
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>
            <CreditCard size={32} style={{ verticalAlign: 'middle', marginRight: '12px' }} />
            Tipi Pagamento
          </h1>
          <div className={styles.pageActions}>
            <ExportDropdown
              onExport={handleExport}
              disabled={tipiPagamento.length === 0}
              loading={exportLoading}
            />
            <button className={styles.btnPrimary} onClick={openNewModal} disabled={loading}>
              <PlusCircle size={18} />
              Nuovo Tipo
            </button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className={styles.errorBanner}>
            <AlertTriangle size={20} />
            {error}
            <button onClick={() => setError(null)} className={styles.btnIcon}>
              <X size={16} />
            </button>
          </div>
        )}

        {/* Stats Bar */}
        <div className={styles.statsBar}>
          <div className={styles.statItem}>
            <CreditCard size={20} style={{ color: 'var(--primary-main)' }} />
            <span className={styles.statNumber}>{loading ? '...' : totalTipi}</span>
            <span className={styles.statText}>Totale</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <Check size={20} style={{ color: 'var(--success-main)' }} />
            <span className={styles.statNumber}>{loading ? '...' : attivi}</span>
            <span className={styles.statText}>Attivi</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <XCircle size={20} style={{ color: 'var(--text-secondary)' }} />
            <span className={styles.statNumber}>{loading ? '...' : inattivi}</span>
            <span className={styles.statText}>Inattivi</span>
          </div>
        </div>

        {/* Table */}
        <div className={styles.card}>
          {loading && (
            <div className={styles.loadingState}>
              Caricamento tipi pagamento...
            </div>
          )}

          {!loading && tipiPagamento.length === 0 && (
            <div className={styles.emptyState}>
              <CreditCard size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
              <div>Nessun tipo pagamento trovato</div>
              <button className={styles.btnPrimary} onClick={openNewModal} style={{ marginTop: '16px' }}>
                <PlusCircle size={18} />
                Crea Primo Tipo
              </button>
            </div>
          )}

          {!loading && tipiPagamento.length > 0 && (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Codice</th>
                    <th>Nome</th>
                    <th>Descrizione</th>
                    <th>Stato</th>
                    <th className={styles.textRight}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {tipiPagamento.map((tipo) => (
                    <tr key={tipo.id}>
                      <td>
                        <span className={styles.codice}>{tipo.codice}</span>
                      </td>
                      <td>
                        <div className={styles.nomeCell}>
                          <CreditCard size={16} style={{ color: 'var(--primary-main)' }} />
                          <span>{tipo.nome}</span>
                        </div>
                      </td>
                      <td className={styles.descrizione}>{tipo.descrizione || '-'}</td>
                      <td>
                        <span className={`${styles.badge} ${tipo.isActive ? styles.badgeSuccess : styles.badgeInactive}`}>
                          {tipo.isActive ? 'Attivo' : 'Inattivo'}
                        </span>
                      </td>
                      <td className={styles.textRight}>
                        <div className={styles.rowActions}>
                          <button className={styles.btnIcon} onClick={() => openEditModal(tipo)} title="Modifica">
                            <Edit size={16} />
                          </button>
                          <button className={styles.btnIcon} onClick={() => openDeleteModal(tipo)} title="Elimina">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Modal Form */}
      {showFormModal && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowFormModal(false); }}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {editingTipo ? <Edit size={24} /> : <PlusCircle size={24} />}
                <span style={{ marginLeft: '8px' }}>
                  {editingTipo ? 'Modifica Tipo Pagamento' : 'Nuovo Tipo Pagamento'}
                </span>
              </h3>
              <button className={styles.modalClose} onClick={() => setShowFormModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Codice</label>
                <input
                  type="text"
                  className={`${styles.formControl} ${formErrors.codice ? styles.error : ''}`}
                  placeholder="Lascia vuoto per generazione automatica"
                  value={formData.codice}
                  onChange={(e) => handleFormChange('codice', e.target.value)}
                />
                {formErrors.codice ? (
                  <span className={styles.errorText}>{formErrors.codice}</span>
                ) : (
                  <span className={styles.helpText}>Se vuoto, verrà generato automaticamente</span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Nome *</label>
                <input
                  type="text"
                  className={`${styles.formControl} ${formErrors.nome ? styles.error : ''}`}
                  placeholder="Es: Bonifico, Contanti, Carta..."
                  value={formData.nome}
                  onChange={(e) => handleFormChange('nome', e.target.value)}
                />
                {formErrors.nome && <span className={styles.errorText}>Campo obbligatorio</span>}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Descrizione</label>
                <textarea
                  className={styles.formControl}
                  rows={3}
                  placeholder="Descrizione opzionale..."
                  value={formData.descrizione}
                  onChange={(e) => handleFormChange('descrizione', e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.checkboxWrapper}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={formData.isActive}
                    onChange={(e) => handleFormChange('isActive', e.target.checked)}
                  />
                  <span>Tipo pagamento attivo</span>
                </label>
              </div>
              <div className={styles.helpText} style={{ marginTop: '16px' }}>
                * Campi obbligatori
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.btnSecondary} onClick={() => setShowFormModal(false)}>
                Annulla
              </button>
              <button className={styles.btnPrimary} onClick={handleSave} disabled={formLoading}>
                <Save size={16} />
                {formLoading ? 'Salvataggio...' : editingTipo ? 'Salva Modifiche' : 'Crea'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Conferma Eliminazione */}
      {showDeleteModal && deleteTarget && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowDeleteModal(false); }}>
          <div className={styles.modalConfirm} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmIcon}>
              <AlertTriangle size={48} />
            </div>
            <h3 className={styles.confirmTitle}>Conferma Eliminazione</h3>
            <p className={styles.confirmText}>
              Sei sicuro di voler eliminare il tipo pagamento <strong>"{deleteTarget.nome}"</strong>?
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.btnSecondary} onClick={() => setShowDeleteModal(false)}>
                Annulla
              </button>
              <button className={styles.btnDanger} onClick={handleDeleteConfirm}>
                <Trash2 size={16} />
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
